import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Briefcase,
    ClipboardCheck,
    CalendarClock,
    AlertTriangle,
    CheckCircle2,
    BellRing,
    Undo2,
    FileInput,
} from "lucide-react";
import { Link } from "react-router-dom";
import { fmtDate, fromNow, STATUS_STYLES, PRIORITY_STYLES } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const KPI = ({ label, value, icon: Icon, tone = "primary", hint }) => (
    <Card className="p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border-slate-200">
        <div className="flex items-start justify-between">
            <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    {label}
                </div>
                <div className="kpi-value text-3xl mt-2 text-slate-900">{value}</div>
                {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
            </div>
            <div
                className={`h-9 w-9 rounded-lg grid place-items-center border ${
                    tone === "warning"
                        ? "bg-amber-50 border-amber-100 text-amber-600"
                        : tone === "danger"
                          ? "bg-rose-50 border-rose-100 text-rose-600"
                          : tone === "success"
                            ? "bg-emerald-50 border-emerald-100 text-emerald-600"
                            : "bg-blue-50 border-blue-100 text-primary"
                }`}
            >
                <Icon size={17} />
            </div>
        </div>
    </Card>
);

export default function EmployeeDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [notifs, setNotifs] = useState([]);
    const [clients, setClients] = useState([]);

    useEffect(() => {
        api.get("/reports/dashboard").then((r) => setData(r.data)).catch(() => {});
        api.get("/notifications").then((r) => setNotifs(r.data.items?.slice(0, 6) || []));
        api.get("/clients").then((r) => setClients(r.data.items || []));
    }, []);

    if (!data) {
        return <div className="text-slate-500 text-sm animate-pulse">Loading dashboard…</div>;
    }

    const pending =
        (data.by_status.assigned || 0) +
        (data.by_status.in_progress || 0) +
        (data.by_status.returned_for_rework || 0);

    return (
        <div className="space-y-8" data-testid="employee-dashboard">
            <PageHeader
                title={`${greeting()}, ${user?.name?.split(" ")[0] || "there"}`}
                subtitle="Your work at a glance — no firm-wide numbers, just what's on your desk."
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI label="My Clients" value={data.totals.my_clients ?? 0} icon={Briefcase} />
                <KPI label="Pending Tasks" value={pending} icon={ClipboardCheck} hint="Assigned + in-progress + rework" />
                <KPI label="Due Today" value={data.totals.due_today || 0} icon={CalendarClock} tone="warning" />
                <KPI label="Overdue" value={data.totals.overdue || 0} icon={AlertTriangle} tone="danger" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">Upcoming deadlines</h3>
                        <Link to="/calendar" className="text-xs text-primary hover:underline">
                            Open calendar
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {data.upcoming_deadlines.length === 0 && (
                            <li className="text-sm text-slate-500 py-6 text-center">
                                No upcoming deadlines. Nice.
                            </li>
                        )}
                        {data.upcoming_deadlines.map((t) => (
                            <li
                                key={t.id}
                                className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
                            >
                                <div className="w-9 h-9 rounded-md bg-blue-50 grid place-items-center text-primary">
                                    <CalendarClock size={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                        {t.title}
                                    </div>
                                    <div className="text-xs text-slate-500">
                                        Due {fmtDate(t.official_deadline)}
                                    </div>
                                </div>
                                <Badge className={`badge-status ${PRIORITY_STYLES[t.priority]}`}>
                                    {t.priority}
                                </Badge>
                            </li>
                        ))}
                    </ul>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">Recent tasks</h3>
                        <Link to="/tasks" className="text-xs text-primary hover:underline">
                            Open my tasks
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {data.recent_tasks.length === 0 && (
                            <li className="text-sm text-slate-500 py-6 text-center">
                                No tasks yet. Check back soon.
                            </li>
                        )}
                        {data.recent_tasks.map((t) => (
                            <li
                                key={t.id}
                                className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
                            >
                                <div className="w-9 h-9 rounded-md bg-slate-50 grid place-items-center text-slate-600">
                                    {t.status === "returned_for_rework" ? (
                                        <Undo2 size={16} className="text-amber-600" />
                                    ) : t.status === "submitted_for_review" ? (
                                        <CheckCircle2 size={16} className="text-violet-600" />
                                    ) : (
                                        <ClipboardCheck size={16} />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                        {t.title}
                                    </div>
                                    <div className="text-xs text-slate-500 capitalize">
                                        {t.compliance_type}
                                    </div>
                                </div>
                                <Badge className={`badge-status ${STATUS_STYLES[t.status] || ""}`}>
                                    {(t.status || "").replace(/_/g, " ")}
                                </Badge>
                            </li>
                        ))}
                    </ul>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">My assigned clients</h3>
                        <Link to="/clients" className="text-xs text-primary hover:underline">
                            View all
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {clients.slice(0, 6).map((c) => (
                            <li
                                key={c.id}
                                className="flex items-center gap-3 p-3 rounded-md border border-slate-200"
                            >
                                <div className="w-9 h-9 rounded-md bg-blue-50 grid place-items-center text-primary">
                                    <Briefcase size={16} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                        {c.name}
                                    </div>
                                    <div className="text-xs text-slate-500">{c.entity_type}</div>
                                </div>
                                <div className="flex gap-1 flex-wrap justify-end">
                                    {(c.compliance_types || []).slice(0, 3).map((t) => (
                                        <span
                                            key={t}
                                            className="badge-status bg-slate-50 text-slate-600 border-slate-200"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </li>
                        ))}
                        {clients.length === 0 && (
                            <li className="text-sm text-slate-500 py-6 text-center">
                                No clients assigned yet.
                            </li>
                        )}
                    </ul>
                </Card>

                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">
                            Pending document requests {docRequests.length > 0 && `(${docRequests.length})`}
                        </h3>
                        <Link to="/documents" className="text-xs text-primary hover:underline">
                            Open documents
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {docRequests.slice(0, 6).map((r) => (
                            <li
                                key={r.id}
                                className="flex items-start gap-3 p-3 rounded-md border border-slate-200"
                                data-testid={`req-widget-${r.id}`}
                            >
                                <div className="w-9 h-9 rounded-md bg-amber-50 text-amber-700 grid place-items-center">
                                    <FileInput size={15} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                        {r.title}
                                    </div>
                                    {r.required_docs?.length > 0 && (
                                        <div className="text-xs text-slate-500 truncate">
                                            {r.required_docs.join(" · ")}
                                        </div>
                                    )}
                                    {r.due_date && (
                                        <div className="text-[11px] text-slate-400 mt-0.5">Due {r.due_date}</div>
                                    )}
                                </div>
                                <Link
                                    to="/documents"
                                    className="text-xs text-primary hover:underline shrink-0"
                                >
                                    Upload
                                </Link>
                            </li>
                        ))}
                        {docRequests.length === 0 && (
                            <li className="text-sm text-slate-500 py-6 text-center">
                                No pending requests.
                            </li>
                        )}
                    </ul>
                </Card>


                <Card className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-semibold">Recent notifications</h3>
                        <Link to="/notifications" className="text-xs text-primary hover:underline">
                            See all
                        </Link>
                    </div>
                    <ul className="space-y-2">
                        {notifs.map((n) => (
                            <li
                                key={n.id}
                                className="flex items-start gap-3 p-3 rounded-md border border-slate-200"
                            >
                                <div className="w-9 h-9 rounded-md bg-blue-50 text-primary grid place-items-center">
                                    <BellRing size={15} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                        {n.title}
                                    </div>
                                    <div className="text-xs text-slate-500 truncate">{n.message}</div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">
                                        {fromNow(n.created_at)}
                                    </div>
                                </div>
                            </li>
                        ))}
                        {notifs.length === 0 && (
                            <li className="text-sm text-slate-500 py-6 text-center">All quiet.</li>
                        )}
                    </ul>
                </Card>
            </div>
        </div>
    );
}

function greeting() {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
}
